import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  extractValidationErrors,
  fetchQuery,
  useMutation
} from '@sequencehq/api'
import { useQuery } from '@sequencehq/api/utils'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { useNotifications } from '@sequencehq/core-components'
import { MINUTES } from '@sequencehq/utils/dates'

import { apiQueryClient } from 'features/api/apiQueryClient'
import { useCreateAggregatedLineItemGroups } from 'CreditNotes/hooks/useCreateAggregatedLineItemGroups'

import { CreditNoteLineItemRow } from './CreditNoteLineItemTable'
import { createCreditNoteWithLineItems } from './createCreditNoteWithLineItems'

/**
 * The "Issue and send" submission flow against the invoice being edited:
 *
 *   1. create a draft credit note with the selected line items
 *      (createCreditNoteWithLineItems, shared with the create-as-draft flow)
 *   2. read the credit note's server-computed balance (its gross total)
 *   3. issue (finalize + send) and apply that balance to the invoice in one call
 *
 * The issue-and-apply endpoint requires an already-created credit note, so creation
 * is a prerequisite step rather than part of the endpoint itself.
 */
export const useIssueAndApplyCreditNote = ({
  invoiceId
}: {
  invoiceId: string
}) => {
  const { displayNotification } = useNotifications()
  const navigate = useNavigate()
  const createLineItemGroups = useCreateAggregatedLineItemGroups()

  // Credit notes are only issued against a SENT invoice, whose details cannot
  // change while the drawer is open, so the cached invoice query (shared with
  // the drawer content) is a safe source for these values.
  const invoiceQuery = useQuery(
    dashboardv99990101Client.getInvoice,
    { id: invoiceId },
    { staleTime: 1 * MINUTES }
  )
  const invoice = invoiceQuery.data

  const { mutateAsync: postCreditNote } = useMutation(
    dashboardv99990101Client.postCreditNote
  )
  const { mutateAsync: issueAndApplyCreditNote } = useMutation(
    dashboard20240730Client.postIssueAndApplyCreditNote
  )

  const issueAndApply = useCallback(
    async (
      rows: CreditNoteLineItemRow[],
      { accountingDate }: { accountingDate: string }
    ): Promise<boolean> => {
      if (!invoice) {
        return false
      }

      try {
        const creditNoteId = await createCreditNoteWithLineItems(
          { postCreditNote, createLineItemGroups },
          { accountingDate, invoice, rows }
        )

        if (!creditNoteId) return false

        // Apply the credit note's full balance. We read it back rather than summing
        // the rows so the applied amount always matches the server-computed gross
        // total (including tax), which is what the ledger balance is initialised to.
        const issuedCreditNote = await fetchQuery(
          apiQueryClient,
          dashboardv99990101Client.getCreditNote,
          { id: creditNoteId },
          { staleTime: 0 }
        )

        if (!issuedCreditNote) return false

        // A retry after a failure re-runs this flow from the top, creating and
        // issuing a fresh credit note; the failed attempt's draft is orphaned.
        // Client-side resumption is deliberately NOT attempted: the backend's
        // issue step is not idempotent (a re-sent issue re-sends the email), so
        // safe retries need backend support — tracked in IE-2772.
        await issueAndApplyCreditNote({
          id: creditNoteId,
          body: {
            invoiceId,
            amount: issuedCreditNote.grossTotal,
            currency: issuedCreditNote.currency
          }
        })

        // The user stays on the invoice; the toast's view button is the way
        // into the newly created credit note. truncate:false keeps the fixed
        // copy from middle-truncating alongside the view button.
        displayNotification('Credit note issued and applied', {
          type: 'success',
          truncate: false,
          'data-testid': 'issueCreditNote.successToast',
          viewLabel: 'View credit note',
          onClickView: () => navigate(`/credit-notes/${creditNoteId}`)
        })

        return true
      } catch (error) {
        const [firstValidationError] = extractValidationErrors(error)
        displayNotification('Failed to issue credit note', {
          type: 'error',
          description: firstValidationError
        })

        return false
      }
    },
    [
      invoiceId,
      invoice,
      postCreditNote,
      createLineItemGroups,
      issueAndApplyCreditNote,
      displayNotification,
      navigate
    ]
  )

  return { issueAndApply }
}
